import { logError, LogCategory } from "utils/sentryLogger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faSpinner, faTrash, faSearch, faInfoCircle, faDownload, faPlay, faClock, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import { Alert, Button, Modal, Form, OverlayTrigger, Tooltip, Tabs, Tab, Toast } from "react-bootstrap";
import { request } from "hooks/useOkoApi/useOkoApi";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { ConsensusActivityRender } from "components/pages/Session/Activity/activities/ConsensusActivity/ConsensusActivityRender";
import { buildPreviewChallenge } from "utils/challenge";
import { colors, constants, generateRoutePath } from "utils";
import { useStandards } from "hooks/useStandards/useStandards";
import { getDomainsByGradeLevel, getClustersByDomain, getStandardsByCluster } from "utils/standards";
import { getGroupedModels, DEFAULT_LLM_MODEL, REASONING_LEVELS, DEFAULT_REASONING_LEVEL } from "constants/llmModels";

const GRADE_OPTIONS = [
  { value: null, label: "All Grades" },
  { value: "K", label: "Kindergarten" },
  { value: "1", label: "Grade 1" },
  { value: "2", label: "Grade 2" },
  { value: "3", label: "Grade 3" },
  { value: "4", label: "Grade 4" },
  { value: "5", label: "Grade 5" },
  { value: "6", label: "Grade 6" },
  { value: "7", label: "Grade 7" },
  { value: "8", label: "Grade 8" },
];

const STATUS_OPTIONS = [
  { value: null, label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const QUESTION_TYPE_OPTIONS = [
  { value: null, label: "All Types" },
  { value: "MULTIPLE_CHOICE", label: "Multiple Choice" },
  { value: "OPEN_RESPONSE", label: "Open Response" },
  { value: "CRAZY_CALCULATOR", label: "Crazy Calculator" },
];

const QUALITY_RATING_OPTIONS = [
  { value: "acceptable", label: "Acceptable" },
  { value: "bad", label: "Bad" },
];


const PROCEDURAL_DIFFICULTY_OPTIONS = [
  { value: "1", label: "Level 1", description: "Numerical, one step, multiple choice or open ended" },
  { value: "2", label: "Level 2", description: "Numerical, multi-step, multiple choice or open ended" },
];

const CONCEPTUAL_DIFFICULTY_OPTIONS = [
  { value: "1", label: "Level 1", description: "Word problem, one step, multiple choice" },
  { value: "2", label: "Level 2", description: "Word problem, one step, open ended" },
  { value: "3", label: "Level 3", description: "Word problem, multi step, multiple choice" },
  { value: "4", label: "Level 4", description: "Word problem, multi step, open ended" },
];

const MATHINESS_OPTIONS = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

const formatStandardOptionLabel = (option) => {
  const colonIndex = option.label?.indexOf(": ");
  if (colonIndex === -1 || colonIndex === undefined) return option.label;
  const code = option.label.slice(0, colonIndex);
  const name = option.label.slice(colonIndex + 2);
  return (
    <span>
      <span style={{ color: "#999" }}>{code}:</span> {name}
    </span>
  );
};

const standardSelectStyles = {
  control: (base) => ({ ...base, minHeight: "42px" }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#f0f0f0" : state.isFocused ? "#f7f7f7" : "white",
    color: "#333",
    ":active": { backgroundColor: "#e8e8e8" },
  }),
};

const REVIEW_STATUS_OPTIONS = [
  { value: "gold", label: "Gold" },
  { value: "reviewed", label: "Reviewed" },
  { value: "unreviewed", label: "Unreviewed" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Default prompt template with expected output format
const DEFAULT_PROMPT_TEMPLATE = `Evaluate each question for quality and difficulty.

Return a JSON array where each item contains "question" and "evaluation" objects.

Expected output format:
[
  {
    "question": {
      "id": "question-uuid",
      "question_type": "MULTIPLE_CHOICE",
      "prompt": "Question text...",
      "standards": ["K.NBT.A.1"],
      "status": "active",
      "answer_options": ["Option A", "Option B", "Option C"]
    },
    "evaluation": {
      "quality_rating": "acceptable",
      "quality_rationale": "Clear question with appropriate visuals",
      "difficulty_level": "medium",
      "difficulty_rationale": "Appropriate for grade level",
      "procedural_level": "1",
      "procedural_rationale": "Single step numerical calculation",
      "conceptual_level": "2",
      "conceptual_rationale": "Word problem, one step, open ended",
      "mathiness_level": "medium",
      "mathiness_rationale": "Moderate mathematical rigor",
      "confidence_score": 0.85,
      "flag_for_review": false
    }
  }
]

Your evaluation instructions:
`;

// Helper to extract error message from various error formats (including Pydantic validation errors)
const extractErrorMessage = (error, fallback = "An error occurred") => {
  if (!error) return fallback;
  if (typeof error === "string") return error;

  // Handle Pydantic validation error array
  if (Array.isArray(error.detail)) {
    return error.detail
      .map((err) => (typeof err === "string" ? err : err?.msg || JSON.stringify(err)))
      .join("; ");
  }

  // Handle single detail object
  if (typeof error.detail === "object" && error.detail?.msg) {
    return error.detail.msg;
  }

  // Handle string detail
  if (typeof error.detail === "string") {
    return error.detail;
  }

  // Handle message property
  if (typeof error.message === "string") {
    return error.message;
  }

  return fallback;
};

export const QuestionHumanAnalyze = () => {
  const { authUserData, loading } = useAuthUserData();
  const history = useHistory();
  const standards = useStandards();

  // Filter state - cascading: Grade → Domain → Cluster → Standard
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedQuestionType, setSelectedQuestionType] = useState(null);

  // Data state
  const [questionsData, setQuestionsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [hasSearched, setHasSearched] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Evaluation form state
  const [qualityRating, setQualityRating] = useState("acceptable");
  const [qualityRationale, setQualityRationale] = useState("");
  const [difficultyLevel, setDifficultyLevel] = useState("medium");
  const [difficultyRationale, setDifficultyRationale] = useState("");
  const [proceduralLevel, setProceduralLevel] = useState(null);
  const [proceduralRationale, setProceduralRationale] = useState("");
  const [conceptualLevel, setConceptualLevel] = useState(null);
  const [conceptualRationale, setConceptualRationale] = useState("");
  const [mathinessLevel, setMathinessLevel] = useState(null);
  const [mathinessRationale, setMathinessRationale] = useState("");
  const [confidenceScore, setConfidenceScore] = useState(0.85);
  const [rationaleDrivers, setRationaleDrivers] = useState([]);
  const [flagForReview, setFlagForReview] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("unreviewed");

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [evaluationToDelete, setEvaluationToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState("expert-seeding");

  // LLM Output tab state
  const [llmSystemPrompt, setLlmSystemPrompt] = useState("");
  const [llmPrompt, setLlmPrompt] = useState(DEFAULT_PROMPT_TEMPLATE);
  const [llmModel, setLlmModel] = useState(DEFAULT_LLM_MODEL);
  const [llmTemperature, setLlmTemperature] = useState(0.7);
  const [llmReasoningLevel, setLlmReasoningLevel] = useState(DEFAULT_REASONING_LEVEL);
  const [llmPreviewQuestion, setLlmPreviewQuestion] = useState(null);
  const [llmBatches, setLlmBatches] = useState([]);
  const [llmSelectedBatch, setLlmSelectedBatch] = useState(null);
  const [llmBatchEvaluations, setLlmBatchEvaluations] = useState([]);
  const [llmSelectedExamples, setLlmSelectedExamples] = useState([]); // IDs of selected example evaluations
  const [llmExamplePreview, setLlmExamplePreview] = useState(null); // Selected example for preview
  const [llmIsRunning, setLlmIsRunning] = useState(false);
  const [llmResults, setLlmResults] = useState([]); // Parsed evaluation results
  const [llmResultsPreview, setLlmResultsPreview] = useState(null); // Selected result for preview
  const [llmResponseModel, setLlmResponseModel] = useState(null); // Model from response
  const [llmResponseTemperature, setLlmResponseTemperature] = useState(null); // Temperature from response
  const [llmError, setLlmError] = useState(null);
  const [llmElapsedTime, setLlmElapsedTime] = useState(0);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState("");

  // LLM tab filter state (separate from Expert Seeding tab)
  const [llmSelectedGrade, setLlmSelectedGrade] = useState(null);
  const [llmSelectedDomain, setLlmSelectedDomain] = useState(null);
  const [llmSelectedCluster, setLlmSelectedCluster] = useState(null);
  const [llmSelectedStandard, setLlmSelectedStandard] = useState(null);
  const [llmSelectedStatus, setLlmSelectedStatus] = useState(null);
  const [llmSelectedQuestionType, setLlmSelectedQuestionType] = useState(null);
  const [llmQuestionsData, setLlmQuestionsData] = useState([]);
  const [llmIsLoadingQuestions, setLlmIsLoadingQuestions] = useState(false);

  const handle403Error = useCallback((error) => {
    const errorStr = extractErrorMessage(error, "");
    if (
      error?.status === 403 ||
      errorStr.includes("403") ||
      errorStr.includes("Forbidden") ||
      errorStr.includes("Access denied")
    ) {
      history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
      return true;
    }
    return false;
  }, [history]);

  // Cascading filter options
  const domainOptions = useMemo(() => {
    if (!selectedGrade || !standards?.length) return [];
    return getDomainsByGradeLevel(standards, selectedGrade);
  }, [standards, selectedGrade]);

  const clusterOptions = useMemo(() => {
    if (!selectedDomain || !standards?.length) return [];
    return getClustersByDomain(standards, selectedDomain);
  }, [standards, selectedDomain]);

  const standardOptions = useMemo(() => {
    if (!selectedCluster || !standards?.length) return [];
    return getStandardsByCluster(standards, selectedCluster);
  }, [standards, selectedCluster]);

  // LLM tab cascading filter options
  const llmDomainOptions = useMemo(() => {
    if (!llmSelectedGrade || !standards?.length) return [];
    return getDomainsByGradeLevel(standards, llmSelectedGrade);
  }, [standards, llmSelectedGrade]);

  const llmClusterOptions = useMemo(() => {
    if (!llmSelectedDomain || !standards?.length) return [];
    return getClustersByDomain(standards, llmSelectedDomain);
  }, [standards, llmSelectedDomain]);

  const llmStandardOptions = useMemo(() => {
    if (!llmSelectedCluster || !standards?.length) return [];
    return getStandardsByCluster(standards, llmSelectedCluster);
  }, [standards, llmSelectedCluster]);

  const fetchQuestions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (selectedStandard) params.append("standard_prefix", selectedStandard);
      if (selectedGrade) params.append("grade", selectedGrade);
      if (selectedStatus) params.append("question_status", selectedStatus);
      if (selectedQuestionType) params.append("question_type", selectedQuestionType);
      params.append("page", currentPage.toString());
      params.append("page_size", pageSize.toString());

      const queryString = params.toString();
      const response = await request(
        `questions/fetch/curation${queryString ? `?${queryString}` : ""}`,
        { method: "GET" }
      );

      setQuestionsData(response.questions || []);
      setTotalCount(response.total_count || 0);
    } catch (e) {
      if (!handle403Error(e)) {
        setError(extractErrorMessage(e, "Failed to fetch questions"));
        logError("Admin question analysis operation failed", e, { category: LogCategory.ERROR });
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedStandard, selectedGrade, selectedStatus, selectedQuestionType, currentPage, pageSize, handle403Error]);


  const handleSearch = () => {
    setCurrentPage(1);
    setHasSearched(true);
    fetchQuestions();
  };

  // Auto-fetch when pagination changes (only after initial search)
  useEffect(() => {
    if (hasSearched) {
      fetchQuestions();
    }
  }, [currentPage, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // LLM Tab Functions
  const fetchLlmQuestions = useCallback(async () => {
    try {
      setLlmIsLoadingQuestions(true);
      setLlmError(null);

      const params = new URLSearchParams();
      if (llmSelectedStandard) params.append("standard_prefix", llmSelectedStandard);
      if (llmSelectedGrade) params.append("grade", llmSelectedGrade);
      if (llmSelectedStatus) params.append("question_status", llmSelectedStatus);
      if (llmSelectedQuestionType) params.append("question_type", llmSelectedQuestionType);
      params.append("page", "1");
      params.append("page_size", "100");

      const queryString = params.toString();
      const response = await request(
        `questions/fetch/curation${queryString ? `?${queryString}` : ""}`,
        { method: "GET" }
      );

      setLlmQuestionsData(response.questions || []);
    } catch (e) {
      if (!handle403Error(e)) {
        setLlmError(extractErrorMessage(e, "Failed to fetch questions"));
        logError("Admin question analysis operation failed", e, { category: LogCategory.ERROR });
      }
    } finally {
      setLlmIsLoadingQuestions(false);
    }
  }, [llmSelectedStandard, llmSelectedGrade, llmSelectedStatus, llmSelectedQuestionType, handle403Error]);

  const fetchLlmBatches = useCallback(async () => {
    try {
      const response = await request("questions/evaluations/batches", { method: "GET" });
      setLlmBatches(response.batches || []);
    } catch (e) {
      if (!handle403Error(e)) {
        logError("Admin question analysis operation failed", e, { category: LogCategory.ERROR });
      }
    }
  }, [handle403Error]);

  const fetchLlmBatchEvaluations = useCallback(async (batchName) => {
    try {
      const response = await request(`questions/evaluations?batch_name=${encodeURIComponent(batchName)}`, { method: "GET" });
      setLlmBatchEvaluations(response.questions || []);
    } catch (e) {
      if (!handle403Error(e)) {
        logError("Admin question analysis operation failed", e, { category: LogCategory.ERROR });
      }
    }
  }, [handle403Error]);

  const runLlmEvaluation = async () => {
    if (!llmPrompt.trim()) {
      setLlmError("Please enter evaluation instructions");
      return;
    }
    if (llmQuestionsData.length === 0) {
      setLlmError("Please load questions first using the filters above");
      return;
    }

    // Start timer
    const startTime = Date.now();
    setLlmElapsedTime(0);
    const timerInterval = setInterval(() => {
      setLlmElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    try {
      setLlmIsRunning(true);
      setLlmError(null);
      setLlmResults([]);
      setLlmResultsPreview(null);
      setLlmResponseModel(null);
      setLlmResponseTemperature(null);

      // Build questions array for API
      const questionsForApi = llmQuestionsData.map((item) => {
        const q = item.question;
        return {
          id: q.id,
          question_type: q.question_type,
          prompt: q.prompt,
          spoken_prompt: q.spoken_prompt,
          standards: q.standards || [],
          status: q.status,
          answer_options: q.options?.map(opt => opt.text) || q.answer_options,
          solution_index: q.solution_index,
          prompt_image_url: q.prompt_image_url,
          difficulty: q.difficulty,
        };
      });

      // Build example evaluations from selected batch evaluations
      const selectedBatchItems = llmBatchEvaluations.filter(
        item => llmSelectedExamples.includes(item.question?.id)
      );
      const exampleEvaluations = selectedBatchItems.map((item) => ({
        question: {
          id: item.question?.id,
          question_type: item.question?.question_type,
          prompt: item.question?.prompt,
          standards: item.question?.standards || [],
          answer_options: item.question?.options?.map(opt => opt.text) || item.question?.answer_options,
        },
        evaluation: {
          quality_rating: item.evaluation?.quality_rating,
          quality_rationale: item.evaluation?.quality_rationale,
          difficulty_level: item.evaluation?.difficulty_level,
          difficulty_rationale: item.evaluation?.difficulty_rationale,
          procedural_level: item.evaluation?.procedural_level,
          procedural_rationale: item.evaluation?.procedural_rationale,
          conceptual_level: item.evaluation?.conceptual_level,
          conceptual_rationale: item.evaluation?.conceptual_rationale,
          mathiness_level: item.evaluation?.mathiness_level,
          mathiness_rationale: item.evaluation?.mathiness_rationale,
          confidence_score: item.evaluation?.confidence_score,
          rationale_drivers: item.evaluation?.rationale_drivers || [],
          flag_for_review: item.evaluation?.flag_for_review || false,
        },
      }));

      const params = {
        model: llmModel,
        temperature: llmTemperature,
        reasoning_effort: llmReasoningLevel === "none" ? undefined : llmReasoningLevel,
        system_prompt: llmSystemPrompt.trim() || undefined,
        prompt: llmPrompt,
        questions: questionsForApi,
        example_evaluations: exampleEvaluations.length > 0 ? exampleEvaluations : undefined,
      };

      console.log("LLM Evaluation params:", params);

      const response = await request("questions/evaluations/llm", {
        method: "POST",
        payload: params,
      });

      console.log("LLM Response:", response);

      // Parse the raw LLM response
      let evaluations = [];
      try {
        // Try to extract JSON array from response (might be wrapped in markdown code blocks)
        const jsonMatch = response.response.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          evaluations = JSON.parse(jsonMatch[0]);
        } else {
          evaluations = JSON.parse(response.response);
        }
      } catch (parseError) {
        console.error("Failed to parse LLM response:", parseError);
        console.log("Raw LLM response:", response.response);
        setLlmError("Failed to parse LLM response. The response is not valid JSON. Check console for raw response.");
        return;
      }

      // Validate that we got an array
      if (!Array.isArray(evaluations)) {
        console.error("LLM response is not an array:", evaluations);
        console.log("Raw LLM response:", response.response);
        setLlmError("Invalid response format. Expected a JSON array. Check console for raw response.");
        return;
      }

      // Store parsed results
      setLlmResults(evaluations);
      setLlmResponseModel(response.model || llmModel);
      setLlmResponseTemperature(response.temperature || llmTemperature);

      if (evaluations.length > 0) {
        setLlmError(null);
        // Show success toast
        setShowSuccessToast(true);
        // Scroll to results section
        setTimeout(() => {
          document.getElementById("llm-results-section")?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    } catch (e) {
      if (!handle403Error(e)) {
        const errorMsg = extractErrorMessage(e, "Failed to run LLM evaluation");
        setLlmError(errorMsg);
        setErrorToastMessage(errorMsg);
        setShowErrorToast(true);
        logError("Admin question analysis operation failed", e, { category: LogCategory.ERROR });
      }
    } finally {
      clearInterval(timerInterval);
      setLlmIsRunning(false);
    }
  };

  const downloadCsv = () => {
    if (!llmResults || llmResults.length === 0) return;

    // Generate CSV from parsed results
    const csvHeaders = [
      "question_id",
      "question_type",
      "quality_rating",
      "quality_rationale",
      "difficulty_level",
      "difficulty_rationale",
      "procedural_level",
      "procedural_rationale",
      "conceptual_level",
      "conceptual_rationale",
      "mathiness_level",
      "mathiness_rationale",
      "confidence_score",
      "rationale_drivers",
      "flag_for_review",
      "model",
      "temperature",
    ];

    const csvRows = llmResults.map((item) => {
      const q = item.question || {};
      const e = item.evaluation || {};
      return [
        q.id || "",
        q.question_type || "",
        e.quality_rating || "",
        `"${(e.quality_rationale || "").replace(/"/g, '""')}"`,
        e.difficulty_level || "",
        `"${(e.difficulty_rationale || "").replace(/"/g, '""')}"`,
        e.procedural_level || "",
        `"${(e.procedural_rationale || "").replace(/"/g, '""')}"`,
        e.conceptual_level || "",
        `"${(e.conceptual_rationale || "").replace(/"/g, '""')}"`,
        e.mathiness_level || "",
        `"${(e.mathiness_rationale || "").replace(/"/g, '""')}"`,
        e.confidence_score || "",
        `"${(e.rationale_drivers || []).join(", ")}"`,
        e.flag_for_review || false,
        llmResponseModel || "",
        llmResponseTemperature || "",
      ];
    });

    const csv = [csvHeaders.join(","), ...csvRows.map((row) => row.join(","))].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `llm-evaluation-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Fetch batches when LLM tab is activated
  useEffect(() => {
    if (activeTab === "llm-output") {
      fetchLlmBatches();
    }
  }, [activeTab, fetchLlmBatches]);

  // Fetch batch evaluations when batch is selected
  useEffect(() => {
    if (llmSelectedBatch) {
      fetchLlmBatchEvaluations(llmSelectedBatch);
    } else {
      setLlmBatchEvaluations([]);
      setLlmSelectedExamples([]);
    }
    // Clear preview when batch changes
    setLlmExamplePreview(null);
  }, [llmSelectedBatch, fetchLlmBatchEvaluations]);

  // Select all evaluations by default when batch evaluations are loaded
  useEffect(() => {
    if (llmBatchEvaluations.length > 0) {
      setLlmSelectedExamples(llmBatchEvaluations.map(item => item.question?.id));
    }
  }, [llmBatchEvaluations]);

  const openEvaluationModal = (questionItem) => {
    setSelectedQuestion(questionItem);

    // Pre-fill form with existing evaluation if present
    if (questionItem.evaluation) {
      const eval_ = questionItem.evaluation;
      setQualityRating(eval_.quality_rating || "acceptable");
      setQualityRationale(eval_.quality_rationale || "");
      setDifficultyLevel(eval_.difficulty_level || "medium");
      setDifficultyRationale(eval_.difficulty_rationale || "");
      setProceduralLevel(eval_.procedural_level || null);
      setProceduralRationale(eval_.procedural_rationale || "");
      setConceptualLevel(eval_.conceptual_level || null);
      setConceptualRationale(eval_.conceptual_rationale || "");
      setMathinessLevel(eval_.mathiness_level || null);
      setMathinessRationale(eval_.mathiness_rationale || "");
      setConfidenceScore(eval_.confidence_score || 0.85);
      setRationaleDrivers(eval_.rationale_drivers || []);
      setFlagForReview(eval_.flag_for_review || false);
      setReviewStatus(eval_.review_status || "unreviewed");
    } else {
      // Reset to defaults
      setQualityRating("acceptable");
      setQualityRationale("");
      setDifficultyLevel("medium");
      setDifficultyRationale("");
      setProceduralLevel(null);
      setProceduralRationale("");
      setConceptualLevel(null);
      setConceptualRationale("");
      setMathinessLevel(null);
      setMathinessRationale("");
      setConfidenceScore(0.85);
      setRationaleDrivers([]);
      setFlagForReview(false);
      setReviewStatus("unreviewed");
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedQuestion(null);
  };

  const saveEvaluation = async () => {
    if (!selectedQuestion) return;

    try {
      setIsSaving(true);
      setError(null);

      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10); // YYYY-MM-DD
      const evaluatorName = authUserData.displayName || "unknown";
      const standard = selectedQuestion.question.standards?.[0] || "unknown";
      const grade = standard.split(".")[0] || "unknown"; // Extract grade from standard (e.g., "K" from "K.CC.A.1")
      const batchName = `${standard}-${grade}-human-${evaluatorName}-${dateStr}`;

      const payload = {
        question_id: selectedQuestion.question.id,
        question_type: selectedQuestion.question.question_type,
        quality_rating: qualityRating,
        quality_rationale: qualityRationale,
        difficulty_level: difficultyLevel,
        difficulty_rationale: difficultyRationale,
        procedural_level: proceduralLevel,
        procedural_rationale: proceduralRationale,
        conceptual_level: conceptualLevel,
        conceptual_rationale: conceptualRationale,
        mathiness_level: mathinessLevel,
        mathiness_rationale: mathinessRationale,
        confidence_score: confidenceScore,
        rationale_drivers: rationaleDrivers,
        flag_for_review: flagForReview,
        review_status: reviewStatus,
        rubric_version: "1.0",
        schema_version: "1.0",
        evaluator_id: authUserData.id,
        evaluator_name: evaluatorName,
        batch_name: batchName,
      };

      await request("questions/evaluations", {
        method: "POST",
        payload,
      });

      closeModal();
      fetchQuestions();
    } catch (e) {
      if (!handle403Error(e)) {
        setError(extractErrorMessage(e, "Failed to save evaluation"));
        logError("Admin question analysis operation failed", e, { category: LogCategory.ERROR });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteConfirm = (questionItem) => {
    setEvaluationToDelete(questionItem);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setEvaluationToDelete(null);
  };

  const deleteEvaluation = async () => {
    if (!evaluationToDelete?.evaluation?.id || isDeleting) return;

    const evaluationId = evaluationToDelete.evaluation.id;

    try {
      setIsDeleting(true);
      setError(null);

      await request(`questions/evaluations/${evaluationId}`, {
        method: "DELETE",
      });

      // Close modal first, then refresh - same as saveEvaluation
      closeDeleteConfirm();
      await fetchQuestions();
    } catch (e) {
      if (!handle403Error(e)) {
        // Ignore 404 - evaluation was already deleted successfully
        if (e?.status !== 404) {
          setError(extractErrorMessage(e, "Failed to delete evaluation"));
          logError("Admin question analysis operation failed", e, { category: LogCategory.ERROR });
        } else {
          // If 404, still close and refresh as deletion succeeded
          closeDeleteConfirm();
          await fetchQuestions();
        }
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endIndex = Math.min(currentPage * pageSize, totalCount);

  const getQuestionTypeBadge = (type) => {
    const badgeColors = {
      MULTIPLE_CHOICE: { bg: colors.blue100, text: colors.blue800 },
      OPEN_RESPONSE: { bg: colors.pink50, text: colors.pink700 },
      CRAZY_CALCULATOR: { bg: "#FFF3E0", text: colors.orange600 },
    };
    const style = badgeColors[type] || { bg: colors.gray100, text: colors.gray700 };
    const labels = {
      MULTIPLE_CHOICE: "MC",
      OPEN_RESPONSE: "OR",
      CRAZY_CALCULATOR: "CC",
    };
    return (
      <span
        style={{
          backgroundColor: style.bg,
          color: style.text,
          padding: "4px 8px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        {labels[type] || type}
      </span>
    );
  };

  const getStatusIndicator = (status) => {
    const isActive = status === "ACTIVE" || status === "active";
    return (
      <span
        style={{
          display: "inline-block",
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          backgroundColor: isActive ? "#00A35A" : colors.gray200,
          marginRight: "6px",
        }}
      />
    );
  };

  const getEvaluationBadge = (evaluation) => {
    if (!evaluation) {
      return (
        <span
          style={{
            backgroundColor: colors.gray100,
            color: colors.gray700,
            padding: "4px 8px",
            borderRadius: "12px",
            fontSize: "12px",
          }}
        >
          Not Evaluated
        </span>
      );
    }

    const qualityColors = {
      acceptable: { bg: "#FFF8E1", text: "#F57F17" },
      bad: { bg: "#FFEBEE", text: "#C62828" },
    };

    const badgeStyle = (bg, text) => ({
      backgroundColor: bg,
      color: text,
      padding: "4px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: 500,
    });

    const qStyle = qualityColors[evaluation.quality_rating] || { bg: colors.gray100, text: colors.gray700 };

    return (
      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
        <span style={badgeStyle(qStyle.bg, qStyle.text)}>
          Q: {evaluation.quality_rating}
        </span>
        {evaluation.procedural_level && (
          <span style={badgeStyle("#f0f4ff", "#6366f1")}>
            P: L{evaluation.procedural_level}
          </span>
        )}
        {evaluation.conceptual_level && (
          <span style={badgeStyle("#fef9f0", "#d97706")}>
            C: L{evaluation.conceptual_level}
          </span>
        )}
        {evaluation.mathiness_level && (
          <span style={badgeStyle("#f0fdf4", "#059669")}>
            M: {evaluation.mathiness_level}
          </span>
        )}
        {evaluation.flag_for_review && (
          <span
            style={{
              backgroundColor: "#FFEBEE",
              color: "#C62828",
              padding: "4px 8px",
              borderRadius: "12px",
              fontSize: "11px",
              fontWeight: 500,
            }}
          >
            Review
          </span>
        )}
        {evaluation.evaluator_name && (
          <span
            style={{
              backgroundColor: colors.gray100,
              color: colors.gray700,
              padding: "4px 8px",
              borderRadius: "12px",
              fontSize: "11px",
              fontWeight: 500,
            }}
          >
            By: {evaluation.evaluator_name}
          </span>
        )}
      </div>
    );
  };

  if (loading || !authUserData) return <></>;
  if (authUserData.isAnonymous) {
    history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
    return null;
  }

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
      {isLoading && (
        <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1000 }}>
          <FontAwesomeIcon icon={faSpinner} spin size="4x" />
        </div>
      )}

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible style={{ marginBottom: "16px" }}>
          {error}
        </Alert>
      )}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
        style={{ borderBottom: `2px solid ${colors.nightlyWoods200}` }}
      >
        <Tab eventKey="expert-seeding" title="Expert Seeding">
          {/* Filters Bar */}
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              marginBottom: "24px",
              marginTop: "16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
          {/* Cascading Standard Filters: Grade → Domain → Cluster → Standard */}
          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>Grade</label>
            <Select
              options={GRADE_OPTIONS}
              value={GRADE_OPTIONS.find((o) => o.value === selectedGrade) || GRADE_OPTIONS[0]}
              onChange={(option) => {
                setSelectedGrade(option?.value || null);
                setSelectedDomain(null);
                setSelectedCluster(null);
                setSelectedStandard(null);
              }}
              placeholder="Select grade..."
              isClearable
            />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginTop: "16px" }}>
          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>Domain</label>
            <Select
              options={domainOptions}
              value={domainOptions.find((o) => o.value === selectedDomain) || null}
              onChange={(option) => {
                setSelectedDomain(option?.value || null);
                setSelectedCluster(null);
                setSelectedStandard(null);
              }}
              placeholder="Select domain..."
              isClearable
              isDisabled={!selectedGrade}
              formatOptionLabel={formatStandardOptionLabel}
              styles={standardSelectStyles}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>Cluster</label>
            <Select
              options={clusterOptions}
              value={clusterOptions.find((o) => o.value === selectedCluster) || null}
              onChange={(option) => {
                setSelectedCluster(option?.value || null);
                setSelectedStandard(null);
              }}
              placeholder="Select cluster..."
              isClearable
              isDisabled={!selectedDomain}
              formatOptionLabel={formatStandardOptionLabel}
              styles={standardSelectStyles}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>Standard</label>
            <Select
              options={standardOptions}
              value={standardOptions.find((o) => o.value === selectedStandard) || null}
              onChange={(option) => setSelectedStandard(option?.value || null)}
              placeholder="Select standard..."
              isClearable
              isDisabled={!selectedCluster}
              formatOptionLabel={formatStandardOptionLabel}
              styles={standardSelectStyles}
            />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginTop: "16px" }}>
          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>Status</label>
            <Select
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find((o) => o.value === selectedStatus) || STATUS_OPTIONS[0]}
              onChange={(option) => setSelectedStatus(option?.value || null)}
              placeholder="Select status..."
              isClearable
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>
              Question Type
            </label>
            <Select
              options={QUESTION_TYPE_OPTIONS}
              value={QUESTION_TYPE_OPTIONS.find((o) => o.value === selectedQuestionType) || QUESTION_TYPE_OPTIONS[0]}
              onChange={(option) => setSelectedQuestionType(option?.value || null)}
              placeholder="Select type..."
              isClearable
            />
          </div>
        </div>

        <div style={{ marginTop: "16px", display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={handleSearch}
            disabled={isLoading}
            style={{
              backgroundColor: colors.nightlyWoods500,
              border: "none",
              borderRadius: "20px",
              padding: "8px 24px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FontAwesomeIcon icon={faSearch} />
            Search
          </Button>
        </div>
      </div>

      {/* Results Summary & Pagination */}
      {!isLoading && totalCount > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            padding: "12px 16px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <span style={{ fontSize: "14px", color: "#666" }}>
              Showing {startIndex}-{endIndex} of {totalCount} questions
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "14px" }}>Page size:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: "4px 8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: "8px 12px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                backgroundColor: currentPage === 1 ? "#f5f5f5" : "white",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                fontSize: "14px",
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: "14px", margin: "0 8px" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: "8px 12px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                backgroundColor: currentPage === totalPages ? "#f5f5f5" : "white",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                fontSize: "14px",
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Questions List */}
      {!isLoading && questionsData.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px", color: "#666" }}>
          No questions found. Try adjusting your filters or click Search to fetch questions.
        </div>
      )}

      {!isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {questionsData.map((item) => (
            <div
              key={item.question.id}
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                display: "flex",
                gap: "24px",
              }}
            >
              {/* Question Preview */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ marginBottom: "12px", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                  {getQuestionTypeBadge(item.question.question_type)}
                  <span style={{ display: "flex", alignItems: "center" }}>
                    {getStatusIndicator(item.question.status)}
                    <span style={{ fontSize: "12px", color: "#666" }}>{item.question.status}</span>
                  </span>
                  {item.question.standards?.map((std) => (
                    <span
                      key={std}
                      style={{
                        backgroundColor: colors.blue50,
                        color: colors.blue800,
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: "11px",
                      }}
                    >
                      {std}
                    </span>
                  ))}
                </div>

                {/* Render question with ConsensusActivityRender for all question types */}
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  backgroundColor: colors.milkGlass500,
                  borderRadius: "8px",
                  padding: "16px",
                  minHeight: "200px",
                }}>
                  <ConsensusActivityRender
                    challenge={buildPreviewChallenge(item.question)}
                    onMCAnswer={() => {}}
                    onORAnswer={() => {}}
                    isMathJaxSet={false}
                    disableOptions={true}
                    showBorder={true}
                    showPromptFormula
                  />
                </div>

                <div style={{ marginTop: "12px", display: "flex", flexWrap: "wrap", gap: "4px 16px", fontSize: "11px", color: "#999" }}>
                  <span>ID: {item.question.id}</span>
                  {item.question.source && <span>Source: {item.question.source}</span>}
                  {item.question.standards?.length > 0 && <span>Standards: {item.question.standards.join(", ")}</span>}
                  <span>Status: {item.question.status?.toUpperCase()}</span>
                </div>
              </div>

              {/* Evaluation Info & Actions */}
              <div
                style={{
                  width: "200px",
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  borderLeft: "1px solid #eee",
                  paddingLeft: "20px",
                }}
              >
                <div>
                  <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px", color: "#666" }}>Evaluation</div>
                  {getEvaluationBadge(item.evaluation)}
                </div>

                {item.evaluation && (
                  <div style={{ fontSize: "11px", color: "#999" }}>
                    <div>Confidence: {Math.round((item.evaluation.confidence_score || 0) * 100)}%</div>
                    {item.evaluation.executed_at && (
                      <div>Date: {new Date(item.evaluation.executed_at).toLocaleDateString()}</div>
                    )}
                  </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "auto" }}>
                  <Button
                    onClick={() => openEvaluationModal(item)}
                    style={{
                      backgroundColor: colors.blue500,
                      border: "none",
                      borderRadius: "20px",
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    {item.evaluation ? "Edit" : "Evaluate"}
                  </Button>

                  {item.evaluation && (
                    <Button
                      onClick={() => openDeleteConfirm(item)}
                      variant="outline-danger"
                      style={{
                        borderRadius: "20px",
                        fontSize: "13px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bottom Pagination */}
      {!isLoading && totalCount > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "24px",
            gap: "8px",
          }}
        >
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              padding: "8px 16px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              backgroundColor: currentPage === 1 ? "#f5f5f5" : "white",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              fontSize: "14px",
            }}
          >
            Previous
          </button>
          <span style={{ fontSize: "14px", margin: "0 16px" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: "8px 16px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              backgroundColor: currentPage === totalPages ? "#f5f5f5" : "white",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              fontSize: "14px",
            }}
          >
            Next
          </button>
        </div>
      )}
        </Tab>

        <Tab eventKey="llm-output" title="LLM Output">
          <div style={{ marginTop: "16px" }}>
            {llmError && (
              <Alert variant="danger" onClose={() => setLlmError(null)} dismissible style={{ marginBottom: "16px" }}>
                {llmError}
              </Alert>
            )}

            {/* Prompt and Model Settings */}
            <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "8px",
                marginBottom: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h5 style={{ marginBottom: "16px", fontWeight: 600 }}>
                LLM Configuration{" "}
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      Configure the AI model settings. Select the model, adjust temperature for response variability, and customize the system prompt and evaluation instructions.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faInfoCircle} style={{ color: colors.gray500, cursor: "help", fontSize: "14px" }} />
                </OverlayTrigger>
              </h5>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 500 }}>
                  System Prompt (Optional){" "}
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        Defines the LLM's persona and expertise. Leave empty to use default:
                        "You are an expert mathematics educator."
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faInfoCircle} style={{ color: colors.gray500, cursor: "help" }} />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={llmSystemPrompt}
                  onChange={(e) => setLlmSystemPrompt(e.target.value)}
                  placeholder="You are an expert mathematics educator."
                />
                <Form.Text className="text-muted">
                  Customize the LLM's role, e.g., "You are a curriculum specialist focusing on grade-level appropriateness."
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 500 }}>
                  Evaluation Instructions{" "}
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        Specify what to evaluate and any additional criteria.
                        The output format template is pre-filled below.
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faInfoCircle} style={{ color: colors.gray500, cursor: "help" }} />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  style={{ minHeight: "700px", fontFamily: "monospace", fontSize: "13px" }}
                  value={llmPrompt}
                  onChange={(e) => setLlmPrompt(e.target.value)}
                />
                <Form.Text className="text-muted">
                  The output format is pre-filled. Add your evaluation criteria after "Your evaluation instructions:".
                </Form.Text>
              </Form.Group>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 500 }}>LLM Model</Form.Label>
                  <Select
                    options={getGroupedModels()}
                    value={getGroupedModels().flatMap(g => g.options).find((o) => o.value === llmModel)}
                    onChange={(option) => setLlmModel(option?.value || DEFAULT_LLM_MODEL)}
                    placeholder="Select model..."
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label style={{ fontWeight: 500 }}>Reasoning Level</Form.Label>
                  <Form.Select
                    value={llmReasoningLevel}
                    onChange={(e) => setLlmReasoningLevel(e.target.value)}
                  >
                    {REASONING_LEVELS.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group>
                  <Form.Label style={{ fontWeight: 500 }}>
                    Temperature: {llmTemperature.toFixed(2)}{" "}
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          Controls randomness in LLM output. Lower values (0.1-0.3) produce more consistent,
                          deterministic results. Higher values (0.7-1.0) increase creativity but may reduce
                          accuracy. For evaluation tasks, 0.7 balances consistency with nuanced judgment.
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faInfoCircle} style={{ color: colors.gray500, cursor: "help" }} />
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Range
                    min={0}
                    max={1}
                    step={0.01}
                    value={llmTemperature}
                    onChange={(e) => setLlmTemperature(Number(e.target.value))}
                  />
                </Form.Group>
              </div>
            </div>

            {/* Filters for Questions */}
            <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "8px",
                marginBottom: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h5 style={{ marginBottom: "16px", fontWeight: 600 }}>
                Question Filters{" "}
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      Filter and load questions to evaluate. Use cascading filters (Grade → Domain → Cluster → Standard) to narrow down the question set.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faInfoCircle} style={{ color: colors.gray500, cursor: "help", fontSize: "14px" }} />
                </OverlayTrigger>
              </h5>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>Grade</label>
                  <Select
                    options={GRADE_OPTIONS}
                    value={GRADE_OPTIONS.find((o) => o.value === llmSelectedGrade) || GRADE_OPTIONS[0]}
                    onChange={(option) => {
                      setLlmSelectedGrade(option?.value || null);
                      setLlmSelectedDomain(null);
                      setLlmSelectedCluster(null);
                      setLlmSelectedStandard(null);
                    }}
                    placeholder="Select grade..."
                    isClearable
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginTop: "16px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>Domain</label>
                  <Select
                    options={llmDomainOptions}
                    value={llmDomainOptions.find((o) => o.value === llmSelectedDomain) || null}
                    onChange={(option) => {
                      setLlmSelectedDomain(option?.value || null);
                      setLlmSelectedCluster(null);
                      setLlmSelectedStandard(null);
                    }}
                    placeholder="Select domain..."
                    isClearable
                    isDisabled={!llmSelectedGrade}
                    formatOptionLabel={formatStandardOptionLabel}
                    styles={standardSelectStyles}
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>Cluster</label>
                  <Select
                    options={llmClusterOptions}
                    value={llmClusterOptions.find((o) => o.value === llmSelectedCluster) || null}
                    onChange={(option) => {
                      setLlmSelectedCluster(option?.value || null);
                      setLlmSelectedStandard(null);
                    }}
                    placeholder="Select cluster..."
                    isClearable
                    isDisabled={!llmSelectedDomain}
                    formatOptionLabel={formatStandardOptionLabel}
                    styles={standardSelectStyles}
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>Standard</label>
                  <Select
                    options={llmStandardOptions}
                    value={llmStandardOptions.find((o) => o.value === llmSelectedStandard) || null}
                    onChange={(option) => setLlmSelectedStandard(option?.value || null)}
                    placeholder="Select standard..."
                    isClearable
                    isDisabled={!llmSelectedCluster}
                    formatOptionLabel={formatStandardOptionLabel}
                    styles={standardSelectStyles}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginTop: "16px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>Status</label>
                  <Select
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find((o) => o.value === llmSelectedStatus) || STATUS_OPTIONS[0]}
                    onChange={(option) => setLlmSelectedStatus(option?.value || null)}
                    placeholder="Select status..."
                    isClearable
                  />
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>Question Type</label>
                  <Select
                    options={QUESTION_TYPE_OPTIONS}
                    value={QUESTION_TYPE_OPTIONS.find((o) => o.value === llmSelectedQuestionType) || QUESTION_TYPE_OPTIONS[0]}
                    onChange={(option) => setLlmSelectedQuestionType(option?.value || null)}
                    placeholder="Select type..."
                    isClearable
                  />
                </div>
              </div>

              <div style={{ marginTop: "16px", display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={fetchLlmQuestions}
                  disabled={llmIsLoadingQuestions}
                  style={{
                    backgroundColor: colors.nightlyWoods500,
                    border: "none",
                    borderRadius: "20px",
                    padding: "8px 24px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {llmIsLoadingQuestions ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faSearch} />
                  )}
                  Load Questions
                </Button>
              </div>
            </div>

            {/* Questions to Evaluate */}
            <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "8px",
                marginBottom: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h5 style={{ marginBottom: "16px", fontWeight: 600 }}>
                Questions to Evaluate{" "}
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      Preview the questions that will be sent to the LLM for evaluation. These questions will be analyzed based on your configuration above.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faInfoCircle} style={{ color: colors.gray500, cursor: "help", fontSize: "14px" }} />
                </OverlayTrigger>
                {llmQuestionsData.length > 0 && (
                  <span style={{ fontWeight: 400, fontSize: "14px", color: colors.gray600, marginLeft: "8px" }}>
                    ({llmQuestionsData.length} question{llmQuestionsData.length !== 1 ? "s" : ""} will be evaluated)
                  </span>
                )}
              </h5>
              <Select
                options={llmQuestionsData.map((item) => ({
                  value: item.question.id,
                  label: `${item.question.id} - ${item.question.prompt?.replace(/<[^>]*>/g, "").substring(0, 80)}...`,
                  question: item.question,
                }))}
                value={llmPreviewQuestion}
                onChange={(selected) => setLlmPreviewQuestion(selected)}
                placeholder="Select a question to preview..."
                isDisabled={llmQuestionsData.length === 0}
                isClearable
                maxMenuHeight={200}
                styles={{
                  menu: (base) => ({ ...base, maxHeight: "200px" }),
                  menuList: (base) => ({ ...base, maxHeight: "200px" }),
                }}
              />
              {llmPreviewQuestion && (
                <div style={{ marginTop: "16px" }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "center",
                    backgroundColor: colors.milkGlass500,
                    borderRadius: "8px",
                    padding: "16px",
                    minHeight: "200px",
                  }}>
                    <ConsensusActivityRender
                      challenge={buildPreviewChallenge(llmPreviewQuestion.question)}
                      onMCAnswer={() => {}}
                      onORAnswer={() => {}}
                      isMathJaxSet={false}
                      disableOptions={true}
                      showBorder={true}
                      showPromptFormula
                    />
                  </div>
                </div>
              )}
              {llmQuestionsData.length === 0 && (
                <div style={{ marginTop: "8px", fontSize: "13px", color: colors.gray500, fontStyle: "italic" }}>
                  Use the filters above and click "Load Questions" to load questions for evaluation.
                </div>
              )}
            </div>

            {/* Batch Evaluations */}
            <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "8px",
                marginBottom: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h5 style={{ marginBottom: "16px", fontWeight: 600 }}>
                Few-Shot Examples (Previous Evaluations){" "}
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      Select human-evaluated examples to guide the LLM's evaluation style. More examples typically improve consistency with human judgments. All evaluations are selected by default.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faInfoCircle} style={{ color: colors.gray500, cursor: "help", fontSize: "14px" }} />
                </OverlayTrigger>
              </h5>
              <Select
                options={llmBatches.map((batch) => ({ value: batch, label: batch }))}
                value={llmSelectedBatch ? { value: llmSelectedBatch, label: llmSelectedBatch } : null}
                onChange={(option) => setLlmSelectedBatch(option?.value || null)}
                placeholder="Select a batch to load examples..."
                isClearable
              />
              {llmBatchEvaluations.length > 0 && (
                <>
                  <div style={{ marginTop: "16px", marginBottom: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "13px", color: colors.gray600 }}>
                      {llmSelectedExamples.length} of {llmBatchEvaluations.length} example(s) selected
                    </span>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setLlmSelectedExamples(llmBatchEvaluations.map(item => item.question?.id))}
                        disabled={llmSelectedExamples.length === llmBatchEvaluations.length}
                      >
                        Select All
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setLlmSelectedExamples([])}
                        disabled={llmSelectedExamples.length === 0}
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>
                  <div style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid #eee", borderRadius: "8px" }}>
                    {llmBatchEvaluations.map((item, idx) => (
                      <OverlayTrigger
                        key={idx}
                        placement="left"
                        flip={true}
                        overlay={
                          <Tooltip id={`tooltip-${idx}`} className="custom-evaluation-tooltip">
                            <div style={{
                              textAlign: "left",
                              padding: "8px 4px",
                              minWidth: "280px",
                              maxWidth: "350px",
                            }}>
                              <div style={{
                                display: "flex",
                                gap: "16px",
                                marginBottom: "10px",
                                paddingBottom: "8px",
                                borderBottom: "1px solid rgba(255,255,255,0.2)"
                              }}>
                                <div>
                                  <div style={{ fontSize: "10px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.5px" }}>Quality</div>
                                  <div style={{ fontSize: "14px", fontWeight: 600, textTransform: "capitalize" }}>{item.evaluation?.quality_rating || "N/A"}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: "10px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.5px" }}>Procedural</div>
                                  <div style={{ fontSize: "14px", fontWeight: 600 }}>{item.evaluation?.procedural_level ? `L${item.evaluation.procedural_level}` : "N/A"}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: "10px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.5px" }}>Conceptual</div>
                                  <div style={{ fontSize: "14px", fontWeight: 600 }}>{item.evaluation?.conceptual_level ? `L${item.evaluation.conceptual_level}` : "N/A"}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: "10px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.5px" }}>Mathiness</div>
                                  <div style={{ fontSize: "14px", fontWeight: 600, textTransform: "capitalize" }}>{item.evaluation?.mathiness_level || "N/A"}</div>
                                </div>
                              </div>
                              {item.evaluation?.quality_rationale && (
                                <div style={{ marginBottom: "8px" }}>
                                  <div style={{ fontSize: "10px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>Quality Rationale</div>
                                  <div style={{ fontSize: "12px", lineHeight: 1.4 }}>{item.evaluation.quality_rationale}</div>
                                </div>
                              )}
                              {item.evaluation?.procedural_rationale && (
                                <div style={{ marginBottom: "8px" }}>
                                  <div style={{ fontSize: "10px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>Procedural Rationale</div>
                                  <div style={{ fontSize: "12px", lineHeight: 1.4 }}>{item.evaluation.procedural_rationale}</div>
                                </div>
                              )}
                              {item.evaluation?.conceptual_rationale && (
                                <div style={{ marginBottom: "8px" }}>
                                  <div style={{ fontSize: "10px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>Conceptual Rationale</div>
                                  <div style={{ fontSize: "12px", lineHeight: 1.4 }}>{item.evaluation.conceptual_rationale}</div>
                                </div>
                              )}
                              {item.evaluation?.mathiness_rationale && (
                                <div style={{ marginBottom: "8px" }}>
                                  <div style={{ fontSize: "10px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>Mathiness Rationale</div>
                                  <div style={{ fontSize: "12px", lineHeight: 1.4 }}>{item.evaluation.mathiness_rationale}</div>
                                </div>
                              )}
                            </div>
                          </Tooltip>
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "10px 12px",
                            borderBottom: idx < llmBatchEvaluations.length - 1 ? "1px solid #f5f5f5" : "none",
                            cursor: "pointer",
                            backgroundColor: llmSelectedExamples.includes(item.question?.id) ? colors.blue50 : "white",
                          }}
                          onClick={() => {
                            if (llmSelectedExamples.includes(item.question?.id)) {
                              setLlmSelectedExamples(llmSelectedExamples.filter(id => id !== item.question?.id));
                            } else {
                              setLlmSelectedExamples([...llmSelectedExamples, item.question?.id]);
                            }
                          }}
                        >
                          <Form.Check
                            type="checkbox"
                            checked={llmSelectedExamples.includes(item.question?.id)}
                            onChange={() => {}}
                            style={{ marginRight: "12px" }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "13px", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {item.question?.prompt?.replace(/<[^>]*>/g, "").substring(0, 60) || "No prompt"}...
                            </div>
                            <div style={{ fontSize: "11px", color: colors.gray500, marginTop: "2px" }}>
                              {item.evaluation?.quality_rating} • P:{item.evaluation?.procedural_level || "-"} C:{item.evaluation?.conceptual_level || "-"} M:{item.evaluation?.mathiness_level || "-"}
                            </div>
                          </div>
                          <FontAwesomeIcon
                            icon={faTrash}
                            style={{ color: colors.gray400, marginLeft: "8px", fontSize: "12px" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLlmSelectedExamples(llmSelectedExamples.filter(id => id !== item.question?.id));
                            }}
                          />
                        </div>
                      </OverlayTrigger>
                    ))}
                  </div>

                  {/* Preview dropdown */}
                  <div style={{ marginTop: "16px" }}>
                    <Form.Label style={{ fontSize: "13px", fontWeight: 500 }}>Preview Example:</Form.Label>
                    <Select
                      options={llmBatchEvaluations.map((item) => ({
                        value: item.question?.id,
                        label: `${item.question?.prompt?.replace(/<[^>]*>/g, "").substring(0, 60)}...`,
                        item: item,
                      }))}
                      value={llmExamplePreview}
                      onChange={(selected) => setLlmExamplePreview(selected)}
                      placeholder="Select an example to preview..."
                      isClearable
                      maxMenuHeight={200}
                    />
                    {llmExamplePreview && (
                      <div style={{ marginTop: "12px" }}>
                        <div style={{
                          display: "flex",
                          justifyContent: "center",
                          backgroundColor: colors.milkGlass500,
                          borderRadius: "8px",
                          padding: "16px",
                          minHeight: "200px",
                        }}>
                          <ConsensusActivityRender
                            challenge={buildPreviewChallenge(llmExamplePreview.item.question)}
                            onMCAnswer={() => {}}
                            onORAnswer={() => {}}
                            isMathJaxSet={false}
                            disableOptions={true}
                            showBorder={true}
                            showPromptFormula
                          />
                        </div>
                        <div style={{
                          marginTop: "12px",
                          padding: "12px",
                          backgroundColor: colors.gray50,
                          borderRadius: "8px",
                          fontSize: "13px",
                        }}>
                          <strong>Human Evaluation:</strong>
                          <div style={{ marginTop: "8px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
                            <span>Quality: <strong>{llmExamplePreview.item.evaluation?.quality_rating}</strong></span>
                            <span>Procedural: <strong>{llmExamplePreview.item.evaluation?.procedural_level ? `Level ${llmExamplePreview.item.evaluation.procedural_level}` : "N/A"}</strong></span>
                            <span>Conceptual: <strong>{llmExamplePreview.item.evaluation?.conceptual_level ? `Level ${llmExamplePreview.item.evaluation.conceptual_level}` : "N/A"}</strong></span>
                            <span>Mathiness: <strong style={{ textTransform: "capitalize" }}>{llmExamplePreview.item.evaluation?.mathiness_level || "N/A"}</strong></span>
                          </div>
                          {llmExamplePreview.item.evaluation?.quality_rationale && (
                            <div style={{ marginTop: "8px" }}>
                              <em>Quality Rationale:</em> {llmExamplePreview.item.evaluation.quality_rationale}
                            </div>
                          )}
                          {llmExamplePreview.item.evaluation?.procedural_rationale && (
                            <div style={{ marginTop: "4px" }}>
                              <em>Procedural Rationale:</em> {llmExamplePreview.item.evaluation.procedural_rationale}
                            </div>
                          )}
                          {llmExamplePreview.item.evaluation?.conceptual_rationale && (
                            <div style={{ marginTop: "4px" }}>
                              <em>Conceptual Rationale:</em> {llmExamplePreview.item.evaluation.conceptual_rationale}
                            </div>
                          )}
                          {llmExamplePreview.item.evaluation?.mathiness_rationale && (
                            <div style={{ marginTop: "4px" }}>
                              <em>Mathiness Rationale:</em> {llmExamplePreview.item.evaluation.mathiness_rationale}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Run LLM and Download */}
            <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "8px",
                marginBottom: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                onClick={runLlmEvaluation}
                disabled={llmIsRunning || llmQuestionsData.length === 0 || !llmPrompt.trim()}
                style={{
                  backgroundColor: colors.blue500,
                  border: "none",
                  borderRadius: "20px",
                  padding: "12px 32px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "16px",
                }}
              >
                {llmIsRunning ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Running LLM...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlay} />
                    Run LLM Evaluation
                  </>
                )}
              </Button>

              {llmIsRunning && (
                <span style={{ marginLeft: "16px", fontSize: "14px", color: colors.gray600, display: "flex", alignItems: "center" }}>
                  <FontAwesomeIcon icon={faClock} style={{ marginRight: "6px" }} />
                  {Math.floor(llmElapsedTime / 60)}:{String(llmElapsedTime % 60).padStart(2, "0")}
                </span>
              )}

              {llmResults.length > 0 && (
                <Button
                  onClick={downloadCsv}
                  style={{
                    backgroundColor: colors.green500,
                    border: "none",
                    borderRadius: "20px",
                    padding: "12px 32px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "16px",
                  }}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Download CSV
                </Button>
              )}
            </div>

            {/* LLM Results Preview */}
            {llmResults.length > 0 && (
              <div
                id="llm-results-section"
                style={{
                  backgroundColor: colors.blue50,
                  padding: "20px",
                  borderRadius: "8px",
                  marginBottom: "24px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                  border: `2px solid ${colors.blue200}`,
                }}
              >
                <h5 style={{ marginBottom: "16px", fontWeight: 600, color: colors.blue700, display: "flex", alignItems: "center", gap: "8px" }}>
                  <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green500 }} />
                  LLM Evaluation Results{" "}
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        Review the AI-generated evaluations. Preview individual results and download as CSV for further analysis.
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faInfoCircle} style={{ color: colors.gray500, cursor: "help", fontSize: "14px" }} />
                  </OverlayTrigger>
                  <span style={{ fontWeight: 400, fontSize: "14px", color: colors.gray600, marginLeft: "auto" }}>
                    ({llmResults.length} evaluation{llmResults.length !== 1 ? "s" : ""} - Valid JSON)
                  </span>
                </h5>
                <Select
                  options={llmResults.map((item, idx) => ({
                    value: idx,
                    label: `${item.question?.id || `Item ${idx + 1}`} - ${item.evaluation?.quality_rating || "N/A"} • P:${item.evaluation?.procedural_level || "-"} C:${item.evaluation?.conceptual_level || "-"} M:${item.evaluation?.mathiness_level || "-"}`,
                    item: item,
                  }))}
                  value={llmResultsPreview}
                  onChange={(selected) => setLlmResultsPreview(selected)}
                  placeholder="Select a result to preview..."
                  isClearable
                  maxMenuHeight={350}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "48px",
                      fontSize: "15px",
                    }),
                    option: (base) => ({
                      ...base,
                      padding: "14px 16px",
                      fontSize: "14px",
                      lineHeight: "1.5",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      fontSize: "15px",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      fontSize: "15px",
                    }),
                  }}
                />
                {llmResultsPreview && (
                  <div style={{ marginTop: "16px" }}>
                    <div style={{
                      backgroundColor: colors.gray50,
                      borderRadius: "8px",
                      padding: "16px",
                      fontFamily: "monospace",
                      fontSize: "12px",
                      maxHeight: "400px",
                      overflowY: "auto",
                    }}>
                      <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {JSON.stringify(llmResultsPreview.item, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Tab>
      </Tabs>

      {/* Evaluation Modal */}
      <Modal show={isModalOpen} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedQuestion?.evaluation ? "Edit Evaluation" : "Create Evaluation"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isSaving && (
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 10 }}>
              <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            </div>
          )}

          <Form style={{ opacity: isSaving ? 0.5 : 1 }}>
            {/* Quality Section */}
            <div style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
              <div style={{ fontWeight: 700, fontSize: "16px", marginBottom: "14px", borderBottom: "2px solid #adb5bd", paddingBottom: "8px" }}>Quality</div>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: "13px" }}>Rating</Form.Label>
                <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
                  {QUALITY_RATING_OPTIONS.map((option) => (
                    <Form.Check
                      key={option.value}
                      type="radio"
                      id={`quality-${option.value}`}
                      name="qualityRating"
                      label={option.label}
                      checked={qualityRating === option.value}
                      onChange={() => setQualityRating(option.value)}
                    />
                  ))}
                </div>
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ fontWeight: 600, fontSize: "13px" }}>Rationale</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={qualityRationale}
                  onChange={(e) => setQualityRationale(e.target.value)}
                  placeholder="Explain the quality rating..."
                  style={{ height: "80px" }}
                />
              </Form.Group>
            </div>

            {/* Procedural Difficulty Section */}
            <div style={{ backgroundColor: "#f0f4ff", border: "1px solid #c7d2fe", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
              <div style={{ fontWeight: 700, fontSize: "16px", marginBottom: "14px", borderBottom: "2px solid #a5b4fc", paddingBottom: "8px" }}>Procedural Difficulty</div>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: "13px" }}>Level</Form.Label>
                <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
                  {PROCEDURAL_DIFFICULTY_OPTIONS.map((option) => (
                    <OverlayTrigger key={option.value} placement="top" overlay={<Tooltip>{option.description}</Tooltip>}>
                      <div>
                        <Form.Check
                          type="radio"
                          id={`procedural-${option.value}`}
                          name="proceduralLevel"
                          label={option.label}
                          checked={proceduralLevel === option.value}
                          onChange={() => setProceduralLevel(option.value)}
                        />
                      </div>
                    </OverlayTrigger>
                  ))}
                </div>
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ fontWeight: 600, fontSize: "13px" }}>Rationale</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={proceduralRationale}
                  onChange={(e) => setProceduralRationale(e.target.value)}
                  placeholder="Explain the procedural difficulty level..."
                  style={{ height: "80px" }}
                />
              </Form.Group>
            </div>

            {/* Conceptual Difficulty Section */}
            <div style={{ backgroundColor: "#fef9f0", border: "1px solid #fcd9a8", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
              <div style={{ fontWeight: 700, fontSize: "16px", marginBottom: "14px", borderBottom: "2px solid #f9b96e", paddingBottom: "8px" }}>Conceptual Difficulty</div>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: "13px" }}>Level</Form.Label>
                <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
                  {CONCEPTUAL_DIFFICULTY_OPTIONS.map((option) => (
                    <OverlayTrigger key={option.value} placement="top" overlay={<Tooltip>{option.description}</Tooltip>}>
                      <div>
                        <Form.Check
                          type="radio"
                          id={`conceptual-${option.value}`}
                          name="conceptualLevel"
                          label={option.label}
                          checked={conceptualLevel === option.value}
                          onChange={() => setConceptualLevel(option.value)}
                        />
                      </div>
                    </OverlayTrigger>
                  ))}
                </div>
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ fontWeight: 600, fontSize: "13px" }}>Rationale</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={conceptualRationale}
                  onChange={(e) => setConceptualRationale(e.target.value)}
                  placeholder="Explain the conceptual difficulty level..."
                  style={{ height: "80px" }}
                />
              </Form.Group>
            </div>

            {/* Mathiness Section */}
            <div style={{ backgroundColor: "#f0fdf4", border: "1px solid #a7f3d0", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
              <div style={{ fontWeight: 700, fontSize: "16px", marginBottom: "14px", borderBottom: "2px solid #6ee7b7", paddingBottom: "8px" }}>Mathiness</div>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: "13px" }}>Level</Form.Label>
                <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
                  {MATHINESS_OPTIONS.map((option) => (
                    <Form.Check
                      key={option.value}
                      type="radio"
                      id={`mathiness-${option.value}`}
                      name="mathinessLevel"
                      label={option.label}
                      checked={mathinessLevel === option.value}
                      onChange={() => setMathinessLevel(option.value)}
                    />
                  ))}
                </div>
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ fontWeight: 600, fontSize: "13px" }}>Rationale</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={mathinessRationale}
                  onChange={(e) => setMathinessRationale(e.target.value)}
                  placeholder="Explain the mathiness level..."
                  style={{ height: "80px" }}
                />
              </Form.Group>
            </div>

            {/* Confidence Score */}
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>Confidence Score: {confidenceScore.toFixed(2)}</Form.Label>
              <Form.Range
                min={0}
                max={1}
                step={0.01}
                value={confidenceScore}
                onChange={(e) => setConfidenceScore(Number(e.target.value))}
              />
            </Form.Group>

            {/* Flag for Review */}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="flagForReview"
                label="Flag for Review"
                checked={flagForReview}
                onChange={(e) => setFlagForReview(e.target.checked)}
              />
            </Form.Group>

            {/* Review Status */}
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>Review Status</Form.Label>
              <Form.Select
                value={reviewStatus}
                onChange={(e) => setReviewStatus(e.target.value)}
              >
                {REVIEW_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeModal}
            disabled={isSaving}
            style={{ borderRadius: "20px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={saveEvaluation}
            disabled={isSaving}
            style={{
              backgroundColor: colors.pink500,
              border: "none",
              borderRadius: "20px",
            }}
          >
            {isSaving ? "Saving..." : "Save Evaluation"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={closeDeleteConfirm}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isDeleting && (
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 10 }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            </div>
          )}
          <p style={{ opacity: isDeleting ? 0.5 : 1 }}>
            Are you sure you want to delete this evaluation? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeDeleteConfirm}
            disabled={isDeleting}
            style={{ borderRadius: "20px" }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={deleteEvaluation}
            disabled={isDeleting}
            style={{ borderRadius: "20px" }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Toast */}
      <Toast
        show={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        delay={5000}
        autohide
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 1050,
          minWidth: "300px",
        }}
        bg="success"
      >
        <Toast.Header>
          <strong className="me-auto" style={{ color: colors.green700 }}>Evaluation Complete</strong>
        </Toast.Header>
        <Toast.Body style={{ color: "white" }}>
          {llmResults.length} evaluation{llmResults.length !== 1 ? "s" : ""} generated in {Math.floor(llmElapsedTime / 60)}:{String(llmElapsedTime % 60).padStart(2, "0")}
        </Toast.Body>
      </Toast>

      {/* Error Toast */}
      <Toast
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        delay={6000}
        autohide
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 1050,
          minWidth: "300px",
        }}
        bg="danger"
      >
        <Toast.Header>
          <strong className="me-auto" style={{ color: "#dc3545" }}>Evaluation Failed</strong>
        </Toast.Header>
        <Toast.Body style={{ color: "white" }}>
          {errorToastMessage}
        </Toast.Body>
      </Toast>
    </div>
  );
};
